/**
 * INP (Interaction to Next Paint) Performance Optimizations
 * Addresses high INP scores (~8.5s desktop, ~11s mobile) by optimizing:
 * - Mobile menu interactions
 * - Swiper initialization
 * - Event handling throttling
 * - DOM manipulation scheduling
 */

// Performance utilities
function scheduleNonCriticalTask(callback) {
  if ('scheduler' in window && 'postTask' in window.scheduler) {
    window.scheduler.postTask(callback, { priority: 'background' });
  } else if ('requestIdleCallback' in window) {
    requestIdleCallback(callback, { timeout: 100 });
  } else {
    setTimeout(callback, 0);
  }
}

function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }
}

// Optimize mobile menu interactions
document.addEventListener('DOMContentLoaded', function() {
  // Throttle menu drawer close operations
  const throttledCloseDrawer = throttle(function() {
    document.documentElement.removeAttribute('scroll-lock');
    const backdrop = document.querySelector('.menu-drawer__backdrop');
    const drawer = document.querySelector('.menu-drawer');
    
    // Use requestAnimationFrame for smooth animations
    requestAnimationFrame(() => {
      if (backdrop) backdrop.style.display = 'none';
      if (drawer) {
        drawer.classList.remove('menu-drawer--open');
        drawer.setAttribute('aria-hidden', 'true');
      }
    });
  }, 100);

  // Replace existing close button handlers
  document.querySelectorAll('.menu-drawer__close-button').forEach(button => {
    // Remove existing listeners by cloning
    const newButton = button.cloneNode(true);
    button.parentNode.replaceChild(newButton, button);
    
    newButton.addEventListener('click', function(e) {
      e.preventDefault();
      throttledCloseDrawer();
    });
  });

  // Optimize backdrop click handling
  const backdrop = document.querySelector('.menu-drawer__backdrop');
  if (backdrop) {
    backdrop.addEventListener('click', function(e) {
      e.preventDefault();
      throttledCloseDrawer();
    });
  }

  // Optimize tab navigation with event delegation
  const tabContainer = document.querySelector('.mobile-menu-tabs');
  if (tabContainer) {
    tabContainer.addEventListener('click', function(e) {
      const tabButton = e.target.closest('.tab-button');
      if (!tabButton) return;

      e.preventDefault();
      
      // Batch DOM updates for tab switching
      requestAnimationFrame(() => {
        const targetId = tabButton.getAttribute('data-target');
        const allTabs = tabContainer.querySelectorAll('.tab-button');
        const allPanels = document.querySelectorAll('.tab-panel');
        
        allTabs.forEach(tab => {
          tab.classList.remove('active');
          tab.setAttribute('aria-selected', 'false');
        });
        
        allPanels.forEach(panel => {
          panel.classList.remove('active');
          panel.setAttribute('aria-hidden', 'true');
        });

        tabButton.classList.add('active');
        tabButton.setAttribute('aria-selected', 'true');
        
        const targetPanel = document.getElementById(targetId);
        if (targetPanel) {
          targetPanel.classList.add('active');
          targetPanel.setAttribute('aria-hidden', 'false');
        }
      });
    });
  }

  // Optimize Swiper initialization with intersection observer
  const swiperElements = document.querySelectorAll('.swiper-container, .product-media-swiper');
  if (swiperElements.length > 0) {
    const swiperObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.swiperInitialized) {
          scheduleNonCriticalTask(() => {
            initializeSwiper(entry.target);
            entry.target.swiperInitialized = true;
          });
        }
      });
    }, { rootMargin: '50px' });

    swiperElements.forEach(element => {
      swiperObserver.observe(element);
    });
  }

  function initializeSwiper(element) {
    // Lazy initialize Swiper to avoid blocking main thread
    if (typeof Swiper !== 'undefined') {
      const swiperConfig = {
        loop: false,
        slidesPerView: 1,
        spaceBetween: 10,
        lazy: true,
        pagination: {
          el: '.swiper-pagination',
          type: 'progressbar',
          clickable: true
        },
        on: {
          init: function() {
            // Preload next slide images
            scheduleNonCriticalTask(() => {
              const nextSlide = this.slides[this.activeIndex + 1];
              if (nextSlide) {
                const img = nextSlide.querySelector('img[data-src]');
                if (img) {
                  img.src = img.dataset.src;
                  img.removeAttribute('data-src');
                }
              }
            });
          }
        }
      };

      new Swiper(element, swiperConfig);
    }
  }

  // Optimize wishlist button interactions with immediate feedback
  document.addEventListener('click', function(e) {
    const wishlistBtn = e.target.closest('.wishlist_button');
    if (wishlistBtn) {
      // Immediate visual feedback
      wishlistBtn.style.pointerEvents = 'none';
      wishlistBtn.style.opacity = '0.7';
      
      // Schedule re-enable after operation
      scheduleNonCriticalTask(() => {
        setTimeout(() => {
          wishlistBtn.style.pointerEvents = 'auto';
          wishlistBtn.style.opacity = '1';
        }, 200);
      });
    }
  });

  // Optimize variant picker interactions
  const variantPickers = document.querySelectorAll('.variant-picker');
  variantPickers.forEach(picker => {
    const throttledVariantChange = throttle(function(event) {
      // Allow original handler to run but prevent rapid changes
      if (event.target.type === 'radio' || event.target.type === 'checkbox') {
        scheduleNonCriticalTask(() => {
          // Defer heavy operations like image updates
          const mediaGallery = document.querySelector('.media-gallery__grid');
          if (mediaGallery) {
            mediaGallery.style.transition = 'opacity 0.3s ease';
          }
        });
      }
    }, 150);

    picker.addEventListener('change', throttledVariantChange);
  });
});

// Add performance monitoring
if (window.PerformanceObserver) {
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.name === 'first-input-delay') {
        console.log('FID:', entry.value);
      }
    }
  });
  
  try {
    observer.observe({ entryTypes: ['first-input'] });
  } catch (e) {
    // Fallback for browsers that don't support first-input
    console.log('First Input Delay monitoring not supported');
  }
}

console.log('✅ INP optimizations loaded');
